'use client';
import { logger } from '../lib/logger';

import { SessionProvider, useSession, signOut } from 'next-auth/react';
import { clearAuthTokenCache } from '@/lib/agentApi';
import { clearSigninLocaleCookie } from '@/lib/signin-locale-cookie';
import { useEffect, useRef, useCallback } from 'react';
import { usePathname } from 'next/navigation';
import { BACKEND_URL } from '@/lib/config';
import { GlobalToastHost } from '@/components/GlobalToastHost';
import { DatadogRum } from '@/components/DatadogRum';
import { RUM_ENABLED } from '@/lib/datadog/env';

/**
 * Session refresh and retry settings for the client auth boundary.
 */
const SESSION_CONFIG = {
  // Refetch session every 5 minutes.
  REFETCH_INTERVAL_SECONDS: 300,

  // Minimum time between window-focus refreshes.
  FOCUS_DEBOUNCE_MS: 5 * 60 * 1000, // 5 minutes

  // Retry a single transient 401 before forcing sign-out.
  MAX_401_RETRIES: 1,

  // Small delay before retrying the failed request.
  RETRY_DELAY_MS: 1000,
};

// Track logout state to prevent multiple redirects
let isLoggingOut = false;

// Track last session validation time for focus debouncing
let lastSessionCheck = Date.now();

// Track 401 retry counts per URL pattern to avoid infinite loops
const retryCountMap = new Map<string, number>();

/**
 * Force logout and redirect to signin
 */
function forceLogout(reason: string) {
  if (isLoggingOut) return;
  isLoggingOut = true;

  logger.info('[SessionGuard] Forcing logout:', reason);
  clearAuthTokenCache();
  clearSigninLocaleCookie();

  signOut({ redirect: false }).then(() => {
    window.location.href = '/auth/signin';
  }).catch(() => {
    window.location.href = '/auth/signin';
  });
}

/**
 * Reset logout state (called when user successfully authenticates)
 */
function resetLogoutState() {
  isLoggingOut = false;
  retryCountMap.clear();
}

/**
 * Get a normalized URL key for retry tracking
 */
function getUrlKey(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    // Group by pathname to avoid tracking query params
    return parsed.pathname;
  } catch {
    return url;
  }
}

/**
 * Check if we should retry a 401 or force logout
 */
function shouldRetry401(url: string): boolean {
  const key = getUrlKey(url);
  const currentRetries = retryCountMap.get(key) || 0;

  if (currentRetries < SESSION_CONFIG.MAX_401_RETRIES) {
    retryCountMap.set(key, currentRetries + 1);
    return true;
  }

  return false;
}

/**
 * Clear retry count for a URL (on successful response)
 */
function clearRetryCount(url: string) {
  const key = getUrlKey(url);
  retryCountMap.delete(key);
}

/**
 * Wraps fetch so API calls get one retry on transient 401 responses.
 */
function SessionGuard({ children }: { children: React.ReactNode }) {
  const { data: session, status, update } = useSession();
  const pathname = usePathname();
  const fetchInterceptorSet = useRef(false);

  // Reset logout state when session becomes authenticated
  useEffect(() => {
    if (status === 'authenticated' && session?.user) {
      resetLogoutState();
      lastSessionCheck = Date.now();
    }
  }, [status, session]);

  // Handle blocked sessions (authenticated but no user data)
  // Wait for session to fully hydrate before checking — on initial load,
  // status can be 'authenticated' briefly before session.user is populated.
  const sessionHydrated = useRef(false);
  useEffect(() => {
    if (status === 'authenticated' && session?.user) {
      sessionHydrated.current = true;
    }
    if (status === 'authenticated' && !session?.user && sessionHydrated.current && !pathname.startsWith('/auth/')) {
      forceLogout('Session authenticated but user data missing');
    }
  }, [session, status, pathname]);

  // Retry one transient 401 per route before forcing sign-out.
  useEffect(() => {
    if (fetchInterceptorSet.current) return;
    fetchInterceptorSet.current = true;

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const url = args[0] instanceof Request ? args[0].url : String(args[0]);

      // Network errors are not auth failures; bubble them to the caller.
      let response: Response;
      try {
        response = await originalFetch(...args);
      } catch (error) {
        // Network error (offline, DNS failure, CORS, backend down, etc.)
        // Re-throw without triggering logout - this is not an auth issue
        throw error;
      }

      // Clear retry count on successful responses
      if (response.ok) {
        clearRetryCount(url);
        return response;
      }

      // Handle 401 responses
      if (response.status === 401 && !window.location.pathname.startsWith('/auth/')) {
        const isApiCall = url.startsWith('/api/') ||
                          url.includes('/api/') ||
                          url.startsWith(BACKEND_URL);

        if (isApiCall) {
          // Skip logout for auth endpoints (expected 401s during login flow)
          if (url.includes('/api/auth/')) {
            return response;
          }

          // Check if we should retry
          if (shouldRetry401(url)) {
            logger.info('[SessionGuard] 401 received, retrying after delay:', url);

            // Wait briefly then retry the request
            await new Promise(resolve => setTimeout(resolve, SESSION_CONFIG.RETRY_DELAY_MS));

            // Try to refresh the session first
            try {
              await update();
            } catch {
              // Session refresh failed, continue with retry anyway
            }

            // Retry the original request
            try {
              const retryResponse = await originalFetch(...args);
              if (retryResponse.ok || retryResponse.status !== 401) {
                clearRetryCount(url);
                return retryResponse;
              }
            } catch (retryError) {
              // Network error on retry - don't logout, just re-throw
              throw retryError;
            }
          }

          // Retries exhausted - this is a real auth failure
          logger.info('[SessionGuard] Persistent 401, forcing logout:', url);
          forceLogout(`Persistent 401 from ${getUrlKey(url)}`);
        }
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
      fetchInterceptorSet.current = false;
    };
  }, [update]);

  return <>{children}</>;
}

/**
 * Custom hook to handle window focus with debouncing
 * Only triggers session refetch if enough time has passed
 */
function useDebouncedWindowFocus(onFocus: () => void) {
  useEffect(() => {
    const handleFocus = () => {
      const now = Date.now();
      const timeSinceLastCheck = now - lastSessionCheck;

      if (timeSinceLastCheck >= SESSION_CONFIG.FOCUS_DEBOUNCE_MS) {
        lastSessionCheck = now;
        onFocus();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [onFocus]);
}

/**
 * Component that handles debounced session refresh on window focus
 */
function SessionFocusHandler() {
  const { update } = useSession();

  const handleFocus = useCallback(() => {
    logger.info('[SessionFocusHandler] Window focused, refreshing session');
    update();
  }, [update]);

  useDebouncedWindowFocus(handleFocus);

  return null;
}

/**
 * App-level provider wrapper for session refresh behavior.
 */
export function Providers({ children }: { children: React.ReactNode }) {
  return (
    <SessionProvider
      // Keep session polling aligned with SESSION_CONFIG.
      refetchInterval={SESSION_CONFIG.REFETCH_INTERVAL_SECONDS}
      // Debounce focus refreshes manually in SessionFocusHandler.
      refetchOnWindowFocus={false}
    >
      <SessionGuard>
        <SessionFocusHandler />
        {RUM_ENABLED && <DatadogRum />}
        <GlobalToastHost />
        {children}
      </SessionGuard>
    </SessionProvider>
  );
}
